import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLudoSocket } from "../components/ludo/useLudoSocket";
import "../components/ludo/LudoBoard.css";
import "../components/ludo/LudoWatch.scss";

// ── Board constants (ported verbatim from the 7wickets client's
// LudoMultiplayer.tsx — this geometry must match the server's board exactly,
// do not "fix"/re-derive it) ─────────────────────────────────────────────
const MAIN_PATH = [
  { r: 13, c: 6 }, { r: 12, c: 6 }, { r: 11, c: 6 }, { r: 10, c: 6 }, { r: 9, c: 6 },
  { r: 8, c: 5 }, { r: 8, c: 4 }, { r: 8, c: 3 }, { r: 8, c: 2 }, { r: 8, c: 1 }, { r: 8, c: 0 },
  { r: 7, c: 0 }, { r: 6, c: 0 },
  { r: 6, c: 1 }, { r: 6, c: 2 }, { r: 6, c: 3 }, { r: 6, c: 4 }, { r: 6, c: 5 },
  { r: 5, c: 6 }, { r: 4, c: 6 }, { r: 3, c: 6 }, { r: 2, c: 6 }, { r: 1, c: 6 }, { r: 0, c: 6 },
  { r: 0, c: 7 }, { r: 0, c: 8 },
  { r: 1, c: 8 }, { r: 2, c: 8 }, { r: 3, c: 8 }, { r: 4, c: 8 }, { r: 5, c: 8 },
  { r: 6, c: 9 }, { r: 6, c: 10 }, { r: 6, c: 11 }, { r: 6, c: 12 }, { r: 6, c: 13 }, { r: 6, c: 14 },
  { r: 7, c: 14 }, { r: 8, c: 14 },
  { r: 8, c: 13 }, { r: 8, c: 12 }, { r: 8, c: 11 }, { r: 8, c: 10 }, { r: 8, c: 9 },
  { r: 9, c: 8 }, { r: 10, c: 8 }, { r: 11, c: 8 }, { r: 12, c: 8 }, { r: 13, c: 8 }, { r: 14, c: 8 },
  { r: 14, c: 7 }, { r: 14, c: 6 },
];
const HOME_STRETCH = {
  red:    [{ r: 13, c: 7 }, { r: 12, c: 7 }, { r: 11, c: 7 }, { r: 10, c: 7 }, { r: 9, c: 7 }, { r: 8, c: 7 }],
  green:  [{ r: 7, c: 1 }, { r: 7, c: 2 }, { r: 7, c: 3 }, { r: 7, c: 4 }, { r: 7, c: 5 }, { r: 7, c: 6 }],
  yellow: [{ r: 1, c: 7 }, { r: 2, c: 7 }, { r: 3, c: 7 }, { r: 4, c: 7 }, { r: 5, c: 7 }, { r: 6, c: 7 }],
  blue:   [{ r: 7, c: 13 }, { r: 7, c: 12 }, { r: 7, c: 11 }, { r: 7, c: 10 }, { r: 7, c: 9 }, { r: 7, c: 8 }],
};
const CENTER = { r: 7, c: 7 };

// Color → track-start offset (server formula: boardSquare = (relativePosition - 1 + colorOffset) % 52).
const PLAYER_START = { red: 0, green: 13, yellow: 26, blue: 39 };
const START_SQUARES = { 0: "red", 13: "green", 26: "yellow", 39: "blue" };
const SAFE_SQUARES = new Set([8, 21, 34, 47]);
const DISPLAY_SHIFT = 0;

const ALL_COLORS = ["red", "green", "yellow", "blue"];
const TOP_ROW_COLORS = ["green", "yellow"];
const BOTTOM_ROW_COLORS = ["red", "blue"];
const RIGHT_SIDE_COLORS = new Set(["yellow", "blue"]);
const MOVER_LABELS = {
  red: "First Mover", green: "Second Mover", yellow: "Third Mover", blue: "Fourth Mover",
};
const TURN_WINDOW_SECS = 8;

// server: 0=yard, 1-52=track, 53-58=home col, 100=done
// local:  -1=yard, 0-51=track, 52-57=home col (6 squares), 58=center
function s2l(p) {
  if (p === 0) return -1;
  if (p >= 100) return 58;
  return p - 1;
}

function getBoardCell(color, pos) {
  if (pos < 0) return null;
  if (pos === 58) return CENTER;
  if (pos >= 52) return HOME_STRETCH[color]?.[pos - 52] ?? null;
  const rel = Math.min(pos + DISPLAY_SHIFT, 50);
  return MAIN_PATH[(PLAYER_START[color] + rel) % 52];
}

function getCellClass(r, c, safe = SAFE_SQUARES) {
  if (r >= 9 && r <= 14 && c >= 0 && c <= 5) return "hb-solid hb-solid-red";
  if (r >= 0 && r <= 5 && c >= 0 && c <= 5)  return "hb-solid hb-solid-green";
  if (r >= 0 && r <= 5 && c >= 9 && c <= 14) return "hb-solid hb-solid-yellow";
  if (r >= 9 && r <= 14 && c >= 9 && c <= 14) return "hb-solid hb-solid-blue";
  if (r >= 6 && r <= 8 && c >= 6 && c <= 8) {
    if (r === 7 && c === 7) return "cell-center";
    if (r === 8 && c === 7) return "cell-tri-red";
    if (r === 7 && c === 6) return "cell-tri-green";
    if (r === 6 && c === 7) return "cell-tri-yellow";
    if (r === 7 && c === 8) return "cell-tri-blue";
    return "cell-center-corner";
  }
  if (c === 7 && r >= 9 && r <= 13) return "cell-stretch-red";
  if (r === 7 && c >= 1 && c <= 5)  return "cell-stretch-green";
  if (c === 7 && r >= 1 && r <= 5)  return "cell-stretch-yellow";
  if (r === 7 && c >= 9 && c <= 13) return "cell-stretch-blue";
  const idx = MAIN_PATH.findIndex(p => p.r === r && p.c === c);
  if (idx !== -1) {
    if (START_SQUARES[idx]) return `cell-safe cell-start cell-start-${START_SQUARES[idx]}`;
    if (safe.has(idx)) return "cell-safe";
    if (idx === 51) return "cell-pre-home-red";
    if (idx === 12) return "cell-pre-home-green";
    if (idx === 25) return "cell-pre-home-yellow";
    if (idx === 38) return "cell-pre-home-blue";
    return "cell-path";
  }
  return "cell-empty";
}

const PIP_PATTERNS = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[28, 22], [72, 22], [28, 50], [72, 50], [28, 78], [72, 78]],
};

function MiniDice({ color, value, rolling, active }) {
  const pips = value && !rolling ? (PIP_PATTERNS[value] || []) : [];
  return (
    <div className={`mini-dice md-${color}${active ? " md-active" : ""}${rolling ? " md-rolling" : ""}`}>
      {rolling ? (
        <span className="md-emoji">🎲</span>
      ) : value ? (
        pips.map(([x, y], i) => (
          <span key={i} className="md-pip" style={{ left: `${x}%`, top: `${y}%` }} />
        ))
      ) : (
        <span className="md-emoji" style={{ opacity: 0.35 }}>🎲</span>
      )}
    </div>
  );
}

const RING_COLORS = { red: "#F44336", blue: "#2196F3", yellow: "#FFC107", green: "#4CAF50" };

function useRemainingMs(deadline) {
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (deadline == null) { setRemaining(null); return; }
    let raf = 0;
    const tick = () => {
      const left = Math.max(0, deadline - Date.now());
      setRemaining(left);
      if (left > 0) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [deadline]);
  return remaining;
}

function BoardCountdown({ deadline, color }) {
  const remainingMs = useRemainingMs(deadline);
  if (remainingMs == null || remainingMs <= 0 || remainingMs > 3000) return null;
  const num = Math.ceil(remainingMs / 1000);
  return (
    <div className="board-countdown">
      <div key={num} className={`board-countdown-num bc-${color}`}>{num}</div>
    </div>
  );
}

// Spectator player panel: every seat renders as an "opponent" (no "You"/roll
// button — a watcher never plays, and the backend rejects roll_dice/
// move_piece from an unassigned connection anyway).
function MPPlayerPanel({ color, isBot, isActive, diceVal, turnKey, mirror, deadline, code, fullName, moverLabel, forfeited }) {
  const showCountdown = isActive;
  const label = isBot
    ? "🤖 Bot"
    : code ? `${code}${fullName ? ` (${fullName})` : ""}` : (moverLabel ?? MOVER_LABELS[color] ?? "Player");

  const remainingMs = useRemainingMs(isActive ? deadline : null);
  const totalMs = TURN_WINDOW_SECS * 1000;
  const arcDeg = showCountdown && remainingMs != null
    ? Math.max(0, (remainingMs / totalMs) * 360)
    : showCountdown ? 360 : 0;
  const ringStyle = showCountdown ? {
    background: `conic-gradient(${RING_COLORS[color] || "#4CAF50"} ${arcDeg}deg, rgba(255,255,255,0.12) ${arcDeg}deg)`,
  } : undefined;

  const avatar = (
    <div key={turnKey} className={`lpc-avatar-ring${showCountdown ? " lpc-ring-on" : ""}`} style={ringStyle}>
      <div className={`lpc-avatar lpc-av-${color}`}>
        {isBot ? (
          <span className="lpc-av-emoji">🤖</span>
        ) : (
          <svg viewBox="0 0 24 24" className="lpc-av-svg" aria-hidden="true">
            <circle cx="12" cy="8.5" r="4.2" />
            <path d="M3.5 21c0-4.4 3.8-7.3 8.5-7.3s8.5 2.9 8.5 7.3z" />
          </svg>
        )}
      </div>
    </div>
  );
  const dice = (
    <div className="lpc-dice">
      <MiniDice color={color} value={diceVal} rolling={false} active={false} />
    </div>
  );

  return (
    <div className={`player-card lpc-${color}${isActive ? " lpc-active" : ""}${mirror ? " lpc-right" : ""}${forfeited ? " lpc-forfeited" : ""}`}>
      <div className="lpc-name-pill">
        <span className="lpc-name">{label}{forfeited ? " (out)" : ""}</span>
      </div>
      <div className="lpc-body">
        {avatar}
        <span className="lpc-divider" />
        {dice}
      </div>
    </div>
  );
}

function QuadrantBadge({ color, player, active }) {
  if (!player) {
    return (
      <div className={`lqb lqb-${color} lqb-empty`}>
        <div className="lqb-circle">
          <span className="lqb-label">Empty</span>
        </div>
        <span className="lqb-mover">Seat unfilled</span>
      </div>
    );
  }
  return (
    <div className={`lqb lqb-${color}${active ? " lqb-active" : ""}`}>
      <div className="lqb-circle">
        <span className="lqb-label">Score</span>
        <span className="lqb-val">{player.score ?? 0}</span>
      </div>
      <span className="lqb-mover">
        {player.isBot
          ? "🤖 Bot"
          : player.code
            ? `${player.code}${player.fullName ? ` (${player.fullName})` : ""}`
            : (player.moverLabel ?? MOVER_LABELS[color])}
      </span>
    </div>
  );
}

function formatClock(total) {
  const t = Math.max(0, Math.floor(total));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

function GameTimer({ secondsLeft, startTime, timeLimit }) {
  const [endAt, setEndAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (secondsLeft != null) { setEndAt(Date.now() + secondsLeft * 1000); return; }
    if (startTime && timeLimit) {
      const start = new Date(startTime).getTime();
      if (!Number.isNaN(start)) setEndAt(start + timeLimit * 1000);
    }
  }, [secondsLeft, startTime, timeLimit]);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (endAt == null) return null;
  const left = Math.max(0, Math.ceil((endAt - now) / 1000));
  const urgent = left <= 60;
  return (
    <div className={`game-timer${urgent ? " gt-urgent" : ""}`}>
      <svg className="gt-icon" viewBox="0 0 32 24" aria-hidden="true">
        <path d="M1 9h6M0 13h6M2 17h5" className="gt-speed" />
        <circle cx="19" cy="13.5" r="8.5" className="gt-face" />
        <path d="M19 9v5l3.2 2" className="gt-hand" />
        <path d="M16.5 3h5M19 3v2" className="gt-hand" />
      </svg>
      <span className="gt-digits">{formatClock(left)}</span>
    </div>
  );
}

const nextTurnFrom = (msg) => {
  const t = msg?.nextTurn ?? msg?.currentTurn ?? msg?.turn;
  return typeof t === "string" && t ? t : null;
};

const findPlayer = (gs, color) =>
  color ? gs?.players?.find(p => p.color?.toLowerCase() === color.toLowerCase()) ?? null : null;

function applyLooseScores(players, msg) {
  let out = players;
  if (msg?.scores && typeof msg.scores === "object" && !Array.isArray(msg.scores)) {
    out = out.map(p => typeof msg.scores[p.color] === "number" ? { ...p, score: msg.scores[p.color] } : p);
  }
  const own = typeof msg?.score === "number" ? msg.score
    : typeof msg?.newScore === "number" ? msg.newScore
    : null;
  if (own != null && msg?.color) {
    out = out.map(p => p.color === msg.color ? { ...p, score: own } : p);
  }
  return out;
}

function mergePlayerStats(players, snapshot) {
  if (!snapshot?.length) return players;
  return players.map(p => {
    const upd = snapshot.find(s => s.color === p.color);
    return upd ? {
      ...p,
      score: typeof upd.score === "number" ? upd.score : p.score,
      piecesHome: typeof upd.piecesHome === "number" ? upd.piecesHome : p.piecesHome,
      isBot: upd.isBot ?? p.isBot,
      moverNumber: upd.moverNumber ?? p.moverNumber,
      moverLabel: upd.moverLabel ?? p.moverLabel,
    } : p;
  });
}

const LudoWatchPage = () => {
  const navigate = useNavigate();
  const { roomCode } = useParams();

  const [gameState, setGameState] = useState(null);
  const [phase, setPhase] = useState("connecting"); // connecting | playing | game_over
  const [diceByColor, setDiceByColor] = useState({});
  const [timerSecs, setTimerSecs] = useState(null);
  const [turnSeq, setTurnSeq] = useState(0);
  const [turnDeadline, setTurnDeadline] = useState(null);
  const [gameOver, setGameOver] = useState(null);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  // ── Step-by-step token animation (purely cosmetic, no server calls) ──
  const STEP_MS = 180;
  const [animPos, setAnimPos] = useState({});
  const animTimers = useRef({});
  const gameStateRef = useRef(null);
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  useEffect(() => () => {
    Object.values(animTimers.current).flat().forEach(clearTimeout);
  }, []);

  const animateMove = useCallback((color, pieceIndex, from, to, killed) => {
    const key = `${color}-${pieceIndex}`;
    (animTimers.current[key] ?? []).forEach(clearTimeout);

    const path = [];
    const last = to >= 100 ? 58 : to;
    for (let p = Math.max(from + 1, from === 0 ? 2 : 1); p <= last; p++) path.push(p);
    if (to >= 100) path.push(100);

    const killKeys = killed.map(k => `${k.color}-${k.pieceIndex}`);
    const done = () => {
      setAnimPos(prev => {
        const next = { ...prev };
        delete next[key];
        killKeys.forEach(k => delete next[k]);
        return next;
      });
      delete animTimers.current[key];
    };

    if (path.length === 0 || path.length > 13) { done(); return; }

    const prevPlayers = gameStateRef.current?.players ?? [];
    const hold = { [key]: from };
    killed.forEach(k => {
      const pl = prevPlayers.find(p => p.color === k.color);
      const pos = pl?.pieces?.[k.pieceIndex];
      if (pos != null) hold[`${k.color}-${k.pieceIndex}`] = pos;
    });
    setAnimPos(prev => ({ ...prev, ...hold }));

    const timers = [];
    path.forEach((pos, i) => {
      timers.push(setTimeout(() => {
        setAnimPos(prev => ({ ...prev, [key]: pos }));
      }, (i + 1) * STEP_MS));
    });
    timers.push(setTimeout(done, (path.length + 1) * STEP_MS));
    animTimers.current[key] = timers;
  }, []);

  const handleMessage = useCallback((msg) => {
    if (msg.type !== "game_state" && msg.type !== "game_over" &&
        (Array.isArray(msg.players) || msg.scores || typeof msg.score === "number" || typeof msg.newScore === "number")) {
      setGameState(prev => prev ? {
        ...prev,
        players: applyLooseScores(mergePlayerStats(prev.players, msg.players), msg),
      } : prev);
    }

    switch (msg.type) {
      case "waiting":
        setPhase("connecting");
        break;
      case "game_state": {
        const g = msg.game ?? msg;
        setGameState(g);
        setPhase("playing");
        break;
      }
      case "player_forfeited":
        showToast(`${msg.color} left the game`);
        setGameState(prev => prev ? {
          ...prev,
          players: prev.players.map(p => p.color === msg.color ? { ...p, forfeited: true } : p),
        } : prev);
        break;
      case "dice_rolled": {
        setDiceByColor(prev => ({ ...prev, [msg.color]: msg.diceValue }));
        const noMoves = Array.isArray(msg.validMoves) && msg.validMoves.length === 0;
        const next = nextTurnFrom(msg);
        if (noMoves) setTurnDeadline(null);
        setGameState(prev => {
          if (!prev) return prev;
          const passTo = noMoves && next && next !== msg.color ? next : null;
          return {
            ...prev,
            diceRolled: !passTo,
            currentTurn: passTo ?? msg.color,
            players: mergePlayerStats(prev.players, msg.players),
          };
        });
        break;
      }
      case "piece_moved": {
        setGameState(prev => prev ? { ...prev, diceRolled: false } : prev);
        setTurnSeq(n => n + 1);
        animateMove(
          msg.color, msg.pieceIndex, msg.from ?? 0, msg.to ?? 0,
          msg.killed && msg.killedPieces?.length ? msg.killedPieces : [],
        );
        if (msg.killed && msg.killedPieces?.length) {
          showToast(`⚔️ ${msg.color} cut a piece!`);
        }
        setGameState(prev => {
          if (!prev) return prev;
          let players = prev.players.map(p => {
            if (p.color !== msg.color) return p;
            const pieces = [...p.pieces];
            pieces[msg.pieceIndex] = msg.to;
            const boardSquares = [...(p.boardSquares ?? [null, null, null, null])];
            boardSquares[msg.pieceIndex] = typeof msg.toBoardSquare === "number" ? msg.toBoardSquare : null;
            return { ...p, pieces, boardSquares };
          });
          if (msg.killed && msg.killedPieces?.length) {
            players = players.map(p => {
              const kills = msg.killedPieces.filter(k => k.color === p.color);
              if (!kills.length) return p;
              const pieces = [...p.pieces];
              const boardSquares = [...(p.boardSquares ?? [null, null, null, null])];
              kills.forEach(k => { pieces[k.pieceIndex] = 0; boardSquares[k.pieceIndex] = null; });
              return { ...p, pieces, boardSquares };
            });
          }
          players = mergePlayerStats(players, msg.players);
          return { ...prev, players };
        });
        break;
      }
      case "turn_changed":
        setGameState(prev => prev ? { ...prev, currentTurn: nextTurnFrom(msg) ?? prev.currentTurn, diceRolled: false } : prev);
        setTurnSeq(n => n + 1);
        if (msg.reason === "three_sixes") showToast("Three sixes! Turn passed");
        break;
      case "turn_missed":
        showToast(
          msg.forfeited
            ? `⛔ ${msg.color} forfeited (3 misses)!`
            : `⏰ ${msg.color} missed their turn (${msg.missCount}/3)`
        );
        if (msg.forfeited) {
          setGameState(prev => prev ? {
            ...prev,
            players: prev.players.map(p => p.color === msg.color ? { ...p, forfeited: true } : p),
          } : prev);
        }
        break;
      case "timer_update":
        setTimerSecs(msg.secondsLeft);
        break;
      case "game_over":
        setGameOver({
          winner: msg.winner, reason: msg.reason, players: msg.players,
          prize: msg.prize, prizePool: msg.prizePool, entryFee: msg.entryFee,
        });
        setPhase("game_over");
        break;
      case "error":
        showToast(msg.message || "Error");
        break;
      default:
        break;
    }
  }, [animateMove]);

  const { reconnecting } = useLudoSocket({ roomCode, onMessage: handleMessage });

  useEffect(() => {
    if (!gameState?.currentTurn || phase !== "playing") { setTurnDeadline(null); return; }
    setTurnDeadline(Date.now() + TURN_WINDOW_SECS * 1000);
  }, [gameState?.currentTurn, turnSeq, phase]);

  const turnKey = `${gameState?.currentTurn ?? "none"}-${gameState?.diceRolled ? 1 : 0}`;

  function buildCellMap() {
    const map = {};
    (gameState?.players ?? []).forEach(player => {
      const color = player.color;
      (player.pieces ?? [0, 0, 0, 0]).forEach((finalPos, idx) => {
        const animating = animPos[`${color}-${idx}`] !== undefined;
        const serverPos = animating ? animPos[`${color}-${idx}`] : finalPos;
        const bs = player.boardSquares?.[idx];
        if (!animating && typeof bs === "number" && serverPos >= 1 && serverPos <= 51 && MAIN_PATH[bs]) {
          const key = `${MAIN_PATH[bs].r},${MAIN_PATH[bs].c}`;
          if (!map[key]) map[key] = [];
          map[key].push({ color, idx, done: false });
          return;
        }
        const localPos = s2l(serverPos);
        const done = localPos === 58;
        const cell = localPos === -1
          ? MAIN_PATH[PLAYER_START[color]]
          : getBoardCell(color, localPos);
        if (!cell) return;
        const key = `${cell.r},${cell.c}`;
        if (!map[key]) map[key] = [];
        map[key].push({ color, idx, done });
      });
    });
    return map;
  }

  if (phase === "connecting") {
    return (
      <div className="mp-screen">
        <div className="mp-card">
          <div className="mp-card-title">🎲 Watching Room {roomCode}</div>
          <div className="mp-spinner" />
          {reconnecting && <p className="mp-reconnect-msg">Reconnecting...</p>}
          <button className="mp-cancel-btn" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>
    );
  }

  if (phase === "game_over" && gameOver) {
    const players = gameOver.players ?? [];
    return (
      <div className="mp-screen">
        <div className="mp-card mp-result-card">
          <div className="mp-result-emoji">🏆</div>
          <div className="mp-result-title">
            {gameOver.winner ? `${gameOver.winner} wins!` : "Game Over"}
          </div>
          {players.length > 0 && (
            <div className="mp-scores">
              {players.map((p) => (
                <div key={p.color} className={`mp-score-row mp-${p.color}`}>
                  <span className="mp-score-color">{p.color}</span>
                  <span className="mp-score-val">{p.score} pts • {p.piecesHome} home</span>
                </div>
              ))}
            </div>
          )}
          <div className="mp-result-btns">
            <button className="mp-btn-secondary" onClick={() => navigate(-1)}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  const cellMap = buildCellMap();
  const midStars = gameState?.safeSquares?.length
    ? new Set(gameState.safeSquares.filter(i => START_SQUARES[i] === undefined))
    : SAFE_SQUARES;
  const turnMsg = `${gameState?.currentTurn ?? ""} is playing...`;

  const renderPanel = (color) => {
    const player = findPlayer(gameState, color);
    if (!player) return <div key={color} className="mp-panel-spacer" />;
    const isActive = gameState?.currentTurn?.toLowerCase() === color;
    return (
      <MPPlayerPanel
        key={color}
        color={color}
        isBot={!!player.isBot}
        isActive={isActive}
        diceVal={diceByColor[color] ?? null}
        turnKey={turnKey}
        mirror={RIGHT_SIDE_COLORS.has(color)}
        deadline={isActive ? turnDeadline : null}
        code={player.code}
        fullName={player.fullName}
        moverLabel={player.moverLabel}
        forfeited={player.forfeited}
      />
    );
  };

  return (
    <div className="mp-game-screen">
      <div className="mp-header mp-header-pool">
        <div className="mp-pool">
          <span className="mp-pool-icon" aria-hidden="true">🏆</span>
          <div className="mp-pool-text">
            <span className="mp-pool-label">Prize Pool</span>
            <span className="mp-pool-amount">₹{(gameState?.prizePool ?? 0).toLocaleString("en-IN")}</span>
          </div>
        </div>
      </div>

      <div className="ludo-players-row">
        {renderPanel(TOP_ROW_COLORS[0])}
        <div className="pp-center-msg gt-center">
          <GameTimer
            secondsLeft={timerSecs}
            startTime={gameState?.startTime}
            timeLimit={gameState?.timeLimit}
          />
          <span className="gt-turn-msg">{turnMsg}</span>
        </div>
        {renderPanel(TOP_ROW_COLORS[1])}
      </div>

      <div className="ludo-board-wrap">
        <div className="ludo-board">
          {Array.from({ length: 15 }, (_, r) =>
            Array.from({ length: 15 }, (_, c) => {
              const key = `${r},${c}`;
              const tokens = cellMap[key] || [];
              return (
                <div key={key} className={`ludo-cell ${getCellClass(r, c, midStars)}`}>
                  {tokens.map(({ color, idx }) => (
                    <div
                      key={`${color}-${idx}`}
                      className={`ludo-token token-${color}${animPos[`${color}-${idx}`] !== undefined ? " token-hop" : ""}`}
                    >
                      <span className="token-inner" />
                    </div>
                  ))}
                </div>
              );
            })
          )}

          {toast && (
            <div className="board-toast-wrap">
              <div key={toast} className="board-toast">{toast}</div>
            </div>
          )}

          <BoardCountdown deadline={turnDeadline} color={gameState?.currentTurn?.toLowerCase() ?? ""} />

          {ALL_COLORS.map(color => (
            <QuadrantBadge
              key={color}
              color={color}
              player={findPlayer(gameState, color)}
              active={gameState?.currentTurn === color}
            />
          ))}
        </div>
      </div>

      <div className="ludo-players-row">
        {renderPanel(BOTTOM_ROW_COLORS[0])}
        <div className="pp-center-msg" />
        {renderPanel(BOTTOM_ROW_COLORS[1])}
      </div>

      {reconnecting && <div className="mp-reconnect-bar">⚡ Reconnecting...</div>}
      <button className="mp-exit-btn" onClick={() => navigate(-1)}>✕ Back</button>
    </div>
  );
};

export default LudoWatchPage;
